import { useConnect, useDisconnect } from 'wagmi'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useOperatorSession } from '@/hooks/useOperatorSession'
import { ACTIVE_CHAINS, chainName } from '@/config/chains'
import { shortAddress } from '@/lib/utils'

export function ConnectGate() {
    const { status, address, consoleEligibilityByChain, signInError, retrySignIn, signOut } =
        useOperatorSession()
    const { connectors, connect, status: connectStatus } = useConnect()
    const { disconnect } = useDisconnect()

    return (
        <div className="bg-background flex min-h-screen items-center justify-center p-6">
            <Card className="w-full max-w-md">
                <CardHeader>
                    <CardTitle>Operator Console</CardTitle>
                    <CardDescription>
                        Connect a wallet that holds a console role on the{' '}
                        <code className="font-mono text-xs">OperatorRoles</code> contract.
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    {status === 'disconnected' && (
                        <div className="space-y-2">
                            {connectors.map((c) => (
                                <Button
                                    key={c.uid}
                                    className="w-full"
                                    disabled={connectStatus === 'pending'}
                                    onClick={() => connect({ connector: c })}
                                >
                                    Connect {c.name}
                                </Button>
                            ))}
                        </div>
                    )}

                    {(status === 'connecting' || status === 'checking-role') && (
                        <p className="text-muted-foreground text-sm">
                            {status === 'connecting'
                                ? 'Connecting wallet…'
                                : 'Checking on-chain role…'}
                        </p>
                    )}

                    {status === 'signing-in' && address && (
                        <div className="space-y-3 text-sm">
                            <p className="font-medium">Sign in as operator</p>
                            <p className="text-muted-foreground">
                                Approve the sign-in request in your wallet to start an operator
                                session.
                            </p>
                            <p className="text-muted-foreground text-xs">
                                <span className="font-mono">{shortAddress(address)}</span>
                            </p>
                        </div>
                    )}

                    {status === 'session-failed' && address && (
                        <div className="space-y-3 text-sm">
                            <p className="text-destructive font-medium">Sign-in failed</p>
                            <p className="text-muted-foreground">
                                {signInError ??
                                    'Wallet signature was rejected or the server declined the request.'}
                            </p>
                            <div className="flex gap-2">
                                <Button size="sm" onClick={retrySignIn}>
                                    Try again
                                </Button>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                        signOut()
                                        disconnect()
                                    }}
                                >
                                    Disconnect
                                </Button>
                            </div>
                        </div>
                    )}

                    {status === 'no-role' && address && (
                        <div className="space-y-2 text-sm">
                            <p className="text-destructive font-medium">Not authorized</p>
                            <p className="text-muted-foreground">
                                <span className="font-mono">{shortAddress(address)}</span> does not
                                hold a console role on any configured chain.
                            </p>
                            <ul className="mt-2 space-y-1 text-xs">
                                {ACTIVE_CHAINS.map((c) => (
                                    <li
                                        key={c.id}
                                        className="border-border flex items-center justify-between rounded border px-2 py-1"
                                    >
                                        <span>{chainName(c.id)}</span>
                                        <span
                                            className={
                                                consoleEligibilityByChain[c.id]
                                                    ? 'text-positive'
                                                    : 'text-muted-foreground'
                                            }
                                        >
                                            {consoleEligibilityByChain[c.id]
                                                ? 'has role'
                                                : 'missing'}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    )
}
